import React from 'react'
import { getSidecarPhoneNumber, webStorage } from 'sidecar-utilities'
import { LinkType } from 'sidecar-components'

/**
 * Generates the help navigation items based on user authentication status and
 * admin impersonation status
 *
 * @param {boolean} authenticated - Whether the user is authenticated
 * @param {boolean} isAshChatGate - Whether Ash is the single chat entry point
 * @param {boolean} isAshFrontDoor - Whether the help icon opens Ash directly
 *   instead of a channel-picking dropdown, and the envelope goes straight to
 *   the inbox instead of a view/send submenu
 * @param {boolean} isOnMessages - Whether the member is already looking at the
 *   messages list. Drops the envelope, which would only link them to the page
 *   they're on. Front-door only — see the envelope's own note.
 * @param {function} openMemberCommPoptart - Function that opens the compose
 *   poptart. Only used on the flag-off path, where the inbox submenu still
 *   offers "Send us a message".
 * @param {function} openSidekick - Function that opens the Ash chat poptart
 * @param {boolean} showSidekick - Whether Ash is available to this member
 * @returns {Array} Help navigation items
 */
export const getHelpNavItems = ({
  authenticated = false,
  isAshChatGate = false,
  isAshFrontDoor = false,
  isOnMessages = false,
  openMemberCommPoptart,
  openSidekick,
  showSidekick = false,
}) => {
  // admin users who are impersonating members should not be able to access everything
  const isAdmin = webStorage.getItem('isAdmin')

  const phoneNumber = getSidecarPhoneNumber()

  // Ash AI menu item is used in both business hours and after hours
  const aiItem = {
    title: 'Chat with Ash AI',
    subtitle: 'Get quick answers 24/7 from Ash, your AI Assistant',
    icon: 'sparkles',
    onClick: e => {
      e.preventDefault()
      if (window.heap) {
        window.heap.track('helpMenuAshClicked')
      }
      openSidekick()
    },
  }

  // When Ash is the chat gate, consolidate into a single "Chat with us" item
  const chatWithUsItem = {
    title: 'Chat with us',
    icon: 'chat',
    onClick: e => {
      e.preventDefault()
      if (window.heap) {
        window.heap.track('helpMenuChatWithUsClicked')
      }
      openSidekick()
    },
  }

  let helpNavItems = []

  // accesiblity controls are only needed on the login page. Once logged in, the button moves to the account menu.
  if (!authenticated) {
    helpNavItems.push({
      icon: 'accessibility',
      title: 'Accessibility controls',
      value: '',
      testId: 'globalNav-accessibility',
      additionalAttributes: {
        'data-acsb-custom-trigger': true,
      },
      //TODO: not sure why we need this onClick handler
      onClick: e => {
        e.preventDefault()
      },
    })
  }

  // a non-logged in user will never have messages
  // and impersonating admins don't need to see the user's messages.
  //
  // Nor does a member already on the messages list: with the front door on the
  // envelope is a plain link to /inbox, so on that page it links to itself and
  // does nothing when tapped (design, 2026-08-21). Front-door only, because with
  // the flag off the envelope is a submenu that still offers "Send us a message"
  // — that one has something to do on the page and stays.
  if (authenticated && !isAdmin && !(isAshFrontDoor && isOnMessages)) {
    // The envelope stays top-level and separate from support, and goes straight
    // to the messages list in one click — the view/send submenu is gone because
    // compose no longer lives there at all. Omitting menuItems is what routes
    // this to HelpNav's plain IconButton branch, which honours value/type as a
    // link. With the front door off, the original submenu is what we roll back
    // to, so compose stays reachable.
    //
    // "Messages", not "Inbox" (2026-08-20): the word members use for the thread
    // Member Care replies in. The route stays `/inbox` — renaming that is a
    // separate, bookmark-breaking change with nothing to do with the copy. The
    // flag-off submenu below keeps its old wording; it is the pre-front-door
    // experience we roll back to, not the one being renamed.
    helpNavItems.push(
      isAshFrontDoor
        ? {
            icon: 'email',
            title: 'Messages',
            testId: 'globalNav-inbox',
            value: '/inbox',
            type: LinkType.INTERNAL,
          }
        : {
            icon: 'email',
            title: 'Inbox',
            testId: 'globalNav-inbox',
            menuItems: [
              {
                title: 'Inbox',
                items: [
                  {
                    icon: 'email',
                    title: 'View messages',
                    path: '/inbox',
                    pathType: LinkType.INTERNAL,
                  },
                  {
                    icon: 'write',
                    title: 'Send us a message',
                    onClick: openMemberCommPoptart,
                  },
                ],
              },
            ],
          },
    )
  }

  // The help menu's sub-items vary based on business hours
  const businessHoursSubItems = []
  const afterHoursSubItems = [
    {
      title: 'Our offices are closed',
      subtitle: (
        <>
          We{`\u2019`}re available <br />
          Mon{`\u2013`}Fri: 8am{`\u2013`}8pm ET, <br />
          Sat{`\u2013`}Sun: 9am{`\u2013`}5pm ET
        </>
      ),
      // Make this disabled but not look disabled
      disabled: true,
      className: '[&_h6]:sh-strong [&_p]:color-textOnDefault',
    },
  ]

  // When Ash is the gate, show a single "Chat with us" item instead of
  // separate Ash AI + Live chat items
  if (isAshChatGate && showSidekick) {
    businessHoursSubItems.push(chatWithUsItem)
  } else if (authenticated && showSidekick) {
    businessHoursSubItems.push(aiItem)
  }

  // Impersonating admins don't need to contact themselves :P
  if (!isAdmin) {
    // Hide "Live chat with Member Care" only when Ash is actively the gate
    // (both flag and sidekick must be enabled)
    if (!(isAshChatGate && showSidekick)) {
      businessHoursSubItems.push({
        title: 'Live chat with Member Care',
        icon: 'chat',
        onClick: e => {
          e.preventDefault()
          if (window.heap) {
            window.heap.track('helpMenuLiveChatClicked')
          }
          if (window.Genesys) {
            window.Genesys('command', 'Messenger.open')
          }
        },
      })
    }

    businessHoursSubItems.push({
      title: `Call us at ${phoneNumber}`,
      icon: 'phone',
      path: `tel:${phoneNumber}`,
      pathType: LinkType.EXTERNAL,
      target: '_self',
      onClick: () => {
        if (window.heap) {
          window.heap.track('helpMenuCallClicked')
        }
      },
    })
  }

  if (isAshChatGate && showSidekick) {
    afterHoursSubItems.push(
      {
        title: 'In the mean time,',
        disabled: true,
        className: '[&_h6]:sh-strong [&_button]:pb-0',
      },
      chatWithUsItem,
      {
        title: 'Read user guides',
        subtitle: 'Answers to common questions',
        icon: 'document',
        path: '/help',
        pathType: LinkType.INTERNAL,
      },
    )
  } else if (authenticated) {
    afterHoursSubItems.push(
      {
        title: 'In the mean time,',
        // Make this disabled but not look disabled
        disabled: true,
        className: '[&_h6]:sh-strong [&_button]:pb-0',
      },
      aiItem,
      {
        title: 'Read user guides',
        subtitle: 'Answers to common questions',
        icon: 'document',
        path: '/help',
        pathType: LinkType.INTERNAL,
      },
    )
  }

  const subItems = _isWithinBusinessHours()
    ? businessHoursSubItems
    : afterHoursSubItems

  // No support icon at all for an impersonating admin. Every branch below leads
  // to a channel that opens a real conversation under the member's identity, and
  // the dropdown has nothing left to show them anyway: in business hours its only
  // entry is Ash, since live chat and the phone number are already `!isAdmin`, so
  // gating Ash alone would render an empty menu. Matches member-app's HelpMenu.
  if (isAdmin) {
    return helpNavItems
  }

  // One obvious place to go: the help icon opens Ash on the first tap instead
  // of asking the member to pick a channel. The dropdown stays as the fallback
  // whenever Ash can't serve them — unauthenticated (Ash needs an auth token) or
  // sidecar-sidekick off (the poptart isn't mounted).
  if (isAshFrontDoor && showSidekick) {
    helpNavItems.push({
      icon: 'tooltip',
      title: 'How can we help?',
      testId: 'globalNav-help',
      onClick: e => {
        e.preventDefault()
        // helpMenuOpened keeps the top-of-funnel "member sought help" metric
        // continuous across the rollout; ashOpenedFromHelpIcon identifies the
        // variant and is the screen-open-without-a-message signal BI needs.
        window.heap?.track('helpMenuOpened')
        window.heap?.track('ashOpenedFromHelpIcon', { source: 'helpIcon' })
        openSidekick()
      },
    })
  } else {
    helpNavItems.push({
      icon: 'tooltip',
      title: 'How can we help?',
      testId: 'globalNav-help',
      menuItems: [
        {
          title: 'How can we help?',
          items: subItems,
        },
      ],
    })
  }

  return helpNavItems
}

let _lastCheck = null
let _lastResult = null

function _isWithinBusinessHours() {
  const now = new Date()

  // Memoize for 1 minute
  if (_lastCheck && now - _lastCheck < 60000) {
    return _lastResult
  }
  _lastCheck = now

  const day = now.getDay() // 0 = Sunday, 1 = Monday, etc.
  const utcHour = now.getUTCHours()

  // Use dynamic DST detection
  const isEasternDST = (date = new Date()) => {
    const jan = new Date(date.getFullYear(), 0, 1)
    const jul = new Date(date.getFullYear(), 6, 1)
    const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset())
    return date.getTimezoneOffset() < stdOffset
  }
  const isDST = isEasternDST(now)
  const etOffset = isDST ? -4 : -5 // EST offset from UTC

  // Convert current time to ET
  const etHour = (utcHour + etOffset + 24) % 24

  let result = false

  // Check weekday hours (Mon-Fri: 8am-8pm ET)
  if (day >= 1 && day <= 5) {
    result = etHour >= 8 && etHour < 20
  }
  // Check weekend hours (Sat-Sun: 9am-5pm ET)
  else if (day === 0 || day === 6) {
    result = etHour >= 9 && etHour < 17
  }

  _lastResult = result
  return result
}

// Function just for the test
export function _resetBusinessHoursMemo() {
  _lastCheck = null
  _lastResult = null
}
